import React, { useState, useEffect } from 'react';
import api from '../services/bookingApi';
import BookingCalendar from './BookingCalendar'; // Import du calendrier

const SalonBookingModule = () => {
  const [services, setServices] = useState([]);
  const [packs, setPacks] = useState([]); // Pour charger les packs si besoin
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('reservation');
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');

  // État pour la nouvelle réservation
  const [bookingData, setBookingData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '10:00',
    serviceIds: [], // Liste des IDs de services sélectionnés
    packId: '',     // ID du pack sélectionné (mutuellement exclusif avec serviceIds idéalement, ou complémentaire)
    notes: ''
  });
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Récupérer la location de l'utilisateur connecté
  const userLocation = localStorage.getItem('userLocation');

  // DEBUG: Vérifier la valeur de userLocation
  console.log('🔍 FRONTEND - userLocation depuis localStorage:', userLocation);
  console.log('🔍 FRONTEND - Type de userLocation:', typeof userLocation);
  console.log('🔍 FRONTEND - userLocation est vide?', !userLocation);

  useEffect(() => {
    loadData();
  }, [userLocation]); // Recharger si la localisation change

  const loadData = async () => {
    setLoading(true);
    try {
      // Nettoyer la localisation (éviter les chaînes "null" ou "undefined")
      const cleanLocation = (userLocation && userLocation !== 'null' && userLocation !== 'undefined') ? userLocation : null;

      const filters = cleanLocation ? { location: cleanLocation } : {};

      console.log('🔍 SalonBookingModule - Filtres nettoyés:', filters);

      const [servicesData, appointmentsData] = await Promise.all([
        api.getServices(filters),
        api.getAppointments(filters)
      ]);

      console.log('📊 Services reçus:', servicesData.length, servicesData);
      console.log('📊 Appointments reçus:', appointmentsData.length, appointmentsData);

      setServices(Array.isArray(servicesData) ? servicesData : []);
      setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);

    } catch (err) {
      console.error(err);
      setError('Erreur de chargement: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({ ...prev, [name]: value }));
  };

  const toggleServiceSelection = (serviceId) => {
    setBookingData(prev => {
      const currentIds = prev.serviceIds;
      if (currentIds.includes(serviceId)) {
        return { ...prev, serviceIds: currentIds.filter(id => id !== serviceId), packId: '' }; // Reset pack si on modifie manuellement
      } else {
        return { ...prev, serviceIds: [...currentIds, serviceId], packId: '' };
      }
    });
  };

  const handleCreateAppointment = async (e, force = false) => {
    if (e) e.preventDefault();
    if (bookingData.serviceIds.length === 0 && !bookingData.packId) {
      alert('Veuillez sélectionner au moins un service ou un pack.');
      return;
    }

    try {
      console.log('🔍 Création rendez-vous - force:', force);

      const payload = {
        ...bookingData,
        location: userLocation,
        forceBooking: force // Passer le flag de forçage
      };

      const response = await api.createAppointment(payload);

      // Si on reçoit une réponse avec un conflit (et qu'on n'a pas déjà forcé)
      if (response.conflict && !force) {
        if (window.confirm(response.message || "Ce créneau est déjà pris. Voulez-vous forcer la réservation ?")) {
          return handleCreateAppointment(null, true);
        }
        return;
      }

      alert('Rendez-vous confirmé !');
      setShowBookingModal(false); // Fermer le modal
      // Reset form
      setBookingData({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        date: new Date().toISOString().split('T')[0],
        startTime: '10:00',
        serviceIds: [],
        packId: '',
        notes: ''
      });
      loadData(); // Recharger le calendrier
    } catch (err) {
      console.error('❌ Erreur création rendez-vous:', err);
      // Gérer l'erreur 409 renvoyée par le backend si le fetch rejette
      if (err.status === 409 || err.message?.includes('disponible')) {
        if (window.confirm("Ce créneau est déjà pris par un autre rendez-vous. Cliquez sur OK pour confirmer quand même, ou Annuler pour changer d'heure.")) {
          return handleCreateAppointment(null, true);
        }
      } else {
        alert('Erreur: ' + (err.message || 'Impossible de créer le rendez-vous'));
      }
    }
  };

  // Calculs pour le résumé
  const selectedServicesList = services.filter(s => bookingData.serviceIds.includes(s._id));
  const totalDuration = selectedServicesList.reduce((acc, s) => acc + s.duration, 0);
  const totalPrice = selectedServicesList.reduce((acc, s) => acc + s.price, 0);

  // Extraire les catégories uniques
  const categories = ['Tous', ...new Set(services.map(s => s.category).filter(Boolean))];

  // Filtrer les services par catégorie
  const filteredServices = selectedCategory === 'Tous'
    ? services
    : services.filter(s => s.category === selectedCategory);

  // Callback quand on clique sur un événement du calendrier
  const handleSelectEvent = (app) => {
    alert(`Rendez-vous de ${app.clientName}\nDate: ${new Date(app.date).toLocaleDateString()}\nHeure: ${app.startTime}\nServices: ${app.services?.map(s => s.name).join(', ')}\nStatut: ${app.status}`);
  };

  const cancelBooking = () => {
    setBookingData(prev => ({ ...prev, serviceIds: [] }));
    setShowBookingModal(false);
  };

  // Styles inline modernes et premium
  const styles = {
    container: {
      maxWidth: '2000px',
      margin: '19px',
      padding: '30px',
      paddingTop: '100px',
      fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    title: {
      fontSize: '36px',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '10px'
    },
    tabs: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      background: 'white',
      padding: '8px',
      borderRadius: '12px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
    },
    tab: {
      flex: 1,
      padding: '14px 24px',
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      fontSize: '15px',
      fontWeight: '600',
      outline: 'none',
      borderRadius: '8px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      color: '#64748b'
    },
    activeTab: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      transform: 'translateY(-2px)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    card: {
      border: '2px solid transparent',
      borderRadius: '16px',
      padding: '20px',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      position: 'relative',
      overflow: 'hidden'
    },
    cardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
    },
    selectedCard: {
      borderColor: '#667eea',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)',
      transform: 'translateY(-4px)'
    },
    cardBadge: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    formSection: {
      background: 'white',
      padding: '30px',
      borderRadius: '16px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      marginTop: '20px'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#334155',
      fontSize: '14px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '15px',
      transition: 'all 0.3s ease',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: 'inherit'
    },
    inputFocus: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
    },
    button: {
      width: '100%',
      padding: '16px 32px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '700',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
    },
    categoryList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '10px',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
      height: 'fit-content'
    },
    categoryBtn: {
      padding: '12px 20px',
      borderRadius: '12px',
      border: '2px solid #f1f5f9',
      background: 'white',
      color: '#64748b',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    activeCategoryBtn: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderColor: 'transparent',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start', // Changé de center pour descendre le popup
      paddingTop: '80px', // Espace depuis le haut
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-out'
    },
    modalContent: {
      background: 'white',
      padding: '40px',
      borderRadius: '30px',
      width: '95%',
      maxWidth: '1000px',
      maxHeight: '95vh',
      boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      position: 'relative',
      overflow: 'visible'
    },
    modalBody: {
      display: 'flex',
      gap: '40px',
      alignItems: 'flex-start'
    },
    floatingSummary: {
      position: 'fixed',
      bottom: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'white',
      padding: '15px 30px',
      borderRadius: '50px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '30px',
      zIndex: 100,
      border: '1px solid #e2e8f0',
      animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    closeButton: {
      position: 'absolute',
      top: '25px',
      right: '25px',
      background: '#fee2e2', // Fond rouge très clair
      border: 'none',
      fontSize: '28px',
      fontWeight: 'bold',
      cursor: 'pointer',
      color: '#ef4444', // Rouge vif
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(239, 68, 68, 0.2)',
      zIndex: 10
    },
    summary: {
      marginBottom: '25px',
      padding: '20px',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
      borderRadius: '12px',
      border: '2px solid #e0e7ff',
      boxShadow: '0 2px 10px rgba(102, 126, 234, 0.1)'
    },
    summaryTitle: {
      fontWeight: '700',
      fontSize: '16px',
      color: '#667eea',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    summaryItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #e0e7ff',
      fontSize: '14px',
      color: '#475569'
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      fontSize: '18px',
      fontWeight: '700',
      color: '#667eea',
      marginTop: '8px'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#94a3b8',
      fontSize: '15px',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
    }
  };

  if (loading) return <div style={styles.container}><div style={{ textAlign: 'center', padding: '40px' }}>⏳ Chargement...</div></div>;

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { transform: translate(-50%, 100%); } to { transform: translate(-50%, 0); } }
          @keyframes modalScale { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          
          .category-scroll::-webkit-scrollbar {
            width: 0px;
          }
        `}
      </style>
      <div style={styles.header}>
        <h1 style={styles.title}>Module de Réservation & Services</h1>
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: '20px', padding: '15px', background: '#fee2e2', borderRadius: '10px', textAlign: 'center' }}>{error}</div>}

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(tab === 'reservation' ? styles.activeTab : {}) }}
          onClick={() => setTab('reservation')}
        >
          📅 Nouvelle Réservation
        </button>
        <button
          style={{ ...styles.tab, ...(tab === 'calendrier' ? styles.activeTab : {}) }}
          onClick={() => setTab('calendrier')}
        >
          🗓️ Calendrier ({appointments.length})
        </button>
      </div>

      {/* ONGLET RESERVATION */}
      {tab === 'reservation' && (
        <div style={{ display: 'flex', gap: '30px', position: 'relative' }}>
          {/* Sidebar - Catégories */}
          <div style={{ width: '250px', flexShrink: 0 }}>
            <h3 style={styles.sectionTitle}>📂 Catégories</h3>
            <div style={styles.categoryList}>
              {categories.map(cat => (
                <button
                  key={cat}
                  style={{
                    ...styles.categoryBtn,
                    ...(selectedCategory === cat ? styles.activeCategoryBtn : {})
                  }}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <span style={{ fontSize: '18px' }}>
                    {cat === 'Tous' ? '✨' : '📁'}
                  </span>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content - Services */}
          <div style={{ flex: 1 }}>
            <h3 style={styles.sectionTitle}>💼 Services disponibles</h3>
            <div style={styles.grid}>
              {filteredServices.map(service => (
                <div
                  key={service._id}
                  style={{
                    ...styles.card,
                    ...(bookingData.serviceIds.includes(service._id) ? styles.selectedCard : {})
                  }}
                  onClick={() => toggleServiceSelection(service._id)}
                  onMouseEnter={(e) => {
                    if (!bookingData.serviceIds.includes(service._id)) {
                      Object.assign(e.currentTarget.style, styles.cardHover);
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!bookingData.serviceIds.includes(service._id)) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = styles.card.boxShadow;
                    }
                  }}
                >
                  <div style={styles.cardBadge}>{service.category}</div>
                  <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px', marginTop: '20px' }}>
                    {service.name}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '15px' }}>
                    {service.description}
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                    <span style={{ color: '#475569', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      ⏱️ {service.duration} min
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#667eea', fontSize: '16px' }}>
                      {service.price} DT
                    </span>
                  </div>
                  {bookingData.serviceIds.includes(service._id) && (
                    <div style={{ position: 'absolute', top: '10px', left: '10px', color: '#667eea', fontSize: '20px' }}>
                      ✅
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredServices.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '40px', marginBottom: '15px' }}>🔍</div>
                Aucun service trouvé dans cette catégorie.
              </div>
            )}
          </div>

          {/* Floating Summary Bar */}
          {bookingData.serviceIds.length > 0 && (
            <div style={styles.floatingSummary}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
                  {bookingData.serviceIds.length} Service(s) sélectionné(s)
                </span>
                <span style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b' }}>
                  Total: {totalPrice} DT
                </span>
              </div>
              <button
                onClick={() => setShowBookingModal(true)}
                style={{
                  ...styles.button,
                  width: 'auto',
                  padding: '12px 30px',
                  borderRadius: '50px'
                }}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.buttonHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = styles.button.boxShadow;
                }}
              >
                Continuer la réservation ➔
              </button>
            </div>
          )}

          {/* Modal Popup Форма */}
          {showBookingModal && (
            <div style={styles.modalOverlay} onClick={cancelBooking}>
              <div
                style={{ ...styles.modalContent, animation: 'modalScale 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
                onClick={e => e.stopPropagation()}
              >
                <button
                  style={styles.closeButton}
                  onClick={cancelBooking}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'rotate(90deg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fee2e2';
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.transform = 'rotate(0deg)';
                  }}
                >
                  ✕
                </button>

                <h2 style={{ ...styles.sectionTitle, fontSize: '28px', marginBottom: '30px', borderBottom: '2px solid #f1f5f9', paddingBottom: '15px' }}>
                  📝 Validation de votre réservation
                </h2>

                <div style={styles.modalBody}>
                  {/* Colonne Gauche - Récapitulatif */}
                  <div style={{ flex: '1', minWidth: '350px' }}>
                    <div style={{ ...styles.summary, margin: 0, height: '100%', boxSizing: 'border-box' }}>
                      <div style={{ ...styles.summaryTitle, fontSize: '20px', marginBottom: '20px' }}>🛒 Récapitulatif des services</div>
                      <div style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '10px' }}>
                        {selectedServicesList.map(s => (
                          <div key={s._id} style={{ ...styles.summaryItem, padding: '12px 0' }}>
                            <span style={{ fontWeight: '500' }}>{s.name}</span>
                            <span style={{ fontWeight: '600' }}>{s.price} DT</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ ...styles.summaryTotal, borderTop: '2px bold #667eea', paddingTop: '20px', marginTop: '20px' }}>
                        <span>Total à régler</span>
                        <span>{totalPrice} DT</span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginTop: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ⏱️ Durée totale estimée : <strong>{totalDuration} min</strong>
                      </div>
                    </div>
                  </div>

                  {/* Colonne Droite - Formulaire */}
                  <div style={{ flex: '1.5', minWidth: '450px' }}>
                    <form onSubmit={handleCreateAppointment}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Nom complet du client *</label>
                        <input
                          type="text" name="clientName" required
                          value={bookingData.clientName} onChange={handleBookingChange}
                          style={styles.input}
                          autoFocus
                          onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                          onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '20px' }}>
                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                          <label style={styles.label}>Adresse Email *</label>
                          <input
                            type="email" name="clientEmail" required
                            value={bookingData.clientEmail} onChange={handleBookingChange}
                            style={styles.input}
                            onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                            onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                          />
                        </div>
                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                          <label style={styles.label}>Numéro de Téléphone *</label>
                          <input
                            type="tel" name="clientPhone" required
                            value={bookingData.clientPhone} onChange={handleBookingChange}
                            style={styles.input}
                            onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                            onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '20px' }}>
                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                          <label style={styles.label}>Date de rendez-vous *</label>
                          <input
                            type="date" name="date" required
                            value={bookingData.date} onChange={handleBookingChange}
                            style={styles.input}
                            onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                            onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                          />
                        </div>
                        <div style={{ ...styles.inputGroup, flex: 1 }}>
                          <label style={styles.label}>Heure de début *</label>
                          <input
                            type="time" name="startTime" required
                            value={bookingData.startTime} onChange={handleBookingChange}
                            style={styles.input}
                            step="900"
                            onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                            onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                          />
                        </div>
                      </div>

                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Notes ou demandes particulières</label>
                        <textarea
                          name="notes"
                          value={bookingData.notes} onChange={handleBookingChange}
                          style={{ ...styles.input, height: '80px', resize: 'none' }}
                          placeholder="Ex: besoin d'un fauteuil spécifique, allergie..."
                          onFocus={(e) => Object.assign(e.currentTarget.style, styles.inputFocus)}
                          onBlur={(e) => e.currentTarget.style.borderColor = styles.input.borderColor}
                        />
                      </div>

                      <button
                        type="submit"
                        style={{ ...styles.button, padding: '20px' }}
                        onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.buttonHover)}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = styles.button.boxShadow;
                        }}
                      >
                        Valider la réservation • {totalPrice} DT
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ONGLET CALENDRIER */}
      {tab === 'calendrier' && (
        <div>
          <h3>Calendrier des rendez-vous</h3>
          <BookingCalendar
            appointments={appointments}
            onSelectEvent={handleSelectEvent}
          />
        </div>
      )}


    </div>
  );
};

export default SalonBookingModule;